// <emma-chat-quick-chips> — horizontal row of refine chip buttons that
// appear in the .refine-zone above the refine-input (DS chats.html
// canonical assembly: chips row precedes the refine input, both inside
// .chat-bubble--emma at the bottom).
//
// Visual: lifts .chip--refine 1:1 from DS chips.css — pill-shaped pastel
// buttons with a `+` icon, tight padding, no border. Container is .ds-row
// with horizontal flex + flex-wrap so chips reflow on narrow widths.
//
// Click → emits `emma-chip-click` (bubbles + composed) with detail
// `{ label, prompt }`. Root T12 catches and re-submits prompt as refine.
//
// Source of chips:
//   - Prefers `chips` prop (LLM-emitted SuggestedChip[]) when present
//   - Otherwise calls getChips() helper which computes deterministic
//     fallback from `results` + `resolvedParams`
//
// Trailing refine-trigger button (DS refine-zone.html `.refine-trigger`):
// when `showTrigger=true`, renders a "Refine search" button after the chips
// inside the same .ds-row (per DS canonical placement). Click toggles
// `triggerOpen` visually and emits `emma-refine-trigger-click` so the
// parent (turn) can show/hide the refine input.

import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type {
  ResultCard,
  SearchParams,
  SuggestedChip,
} from '../types/search.js';
import { designTokens } from './design-tokens.js';
import { getChips } from './quick-chips-helper.js';
import { handleTooltipClampEvent } from './tooltip-clamp.js';

@customElement('emma-chat-quick-chips')
export class EmmaChatQuickChips extends LitElement {
  /** LLM-emitted refine chips (preferred when non-empty). */
  @property({ attribute: false })
  chips: SuggestedChip[] = [];

  /** Results from the current turn, used to derive contextual chips. */
  @property({ attribute: false })
  results: ResultCard[] = [];

  /** Resolved params from the current turn, used for fallback chip logic. */
  @property({ attribute: false })
  resolvedParams: SearchParams | null = null;

  /** When true, renders a trailing .refine-trigger button at the end of the chip row. */
  @property({ type: Boolean, attribute: 'show-trigger' })
  showTrigger = false;

  /** Visual state of the trigger — drives the .refine-trigger--open class
   *  (icon flip + label hide). Parent owns the open/closed source of truth. */
  @property({ type: Boolean, attribute: 'trigger-open' })
  triggerOpen = false;

  /** Provenance of these chips. `'help'` = follow-up chips on a grounded
   *  consultative (Docsie) answer — a click should re-enter the grounding lane,
   *  not config-search (QA #10). Empty/default = ordinary refine chips. Forwarded
   *  verbatim in the emma-chip-click detail so the root can tag the search. */
  @property({ type: String })
  origin = '';

  static override styles = [
    designTokens,
    // Lifted 1:1 from design-system/components/chips.css `.chip--refine`.
    // .ds-row layout matches chats.html refine-zone container.
    css`
      :host {
        display: block;
      }
      .ds-row {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-1);
        align-items: center;
      }
      .chip--refine {
        display: inline-flex;
        align-items: center;
        gap: var(--space-1);
        padding: 4px 12px;
        border-radius: var(--radius-full);
        border: none;
        background: var(--gray-10);
        font-family: var(--font-sans);
        font-size: var(--text-xs);
        color: var(--color-text-muted);
        cursor: pointer;
        transition:
          background var(--duration-fast) var(--easing),
          color var(--duration-fast) var(--easing);
        white-space: nowrap;
        user-select: none;
        position: relative; /* anchor for the custom tooltip (QA #9) */
      }
      .chip--refine svg {
        flex-shrink: 0;
        opacity: 0.6;
      }

      /* QA #9 — custom CSS tooltip replacing the native \`title\` attribute,
         which has an untunable ~0.5–1.5s browser show-delay. Shows the
         full chip prompt (works with #11's now-full prompt) with a short,
         controlled ~150ms delay. Accessible name is preserved via
         aria-label on the button. */
      .chip--refine[data-tooltip]::after {
        content: attr(data-tooltip);
        position: absolute;
        bottom: calc(100% + 6px);
        left: 50%;
        transform: translateX(-50%);
        /* Design-review fix 3 — the JS assist (tooltip-clamp.ts) caps the
           width to the chat frame via --emma-tt-max; long texts WRAP. */
        max-width: var(--emma-tt-max, 260px);
        width: max-content;
        padding: 6px 10px;
        background: var(--gray-100);
        color: var(--white);
        font-family: var(--font-sans);
        font-size: 11px;
        font-weight: var(--font-medium);
        line-height: 1.4;
        border-radius: var(--radius-sm);
        white-space: normal;
        text-align: left;
        pointer-events: none;
        opacity: 0;
        z-index: 100;
        transition: opacity var(--duration-fast) var(--easing) 150ms;
      }
      /* Design-review fix 3 — edge-aware alignment. tooltip-clamp.ts sets
         data-tt-align when the centered tooltip would cross the chat-frame
         edge: near the right edge the tooltip's right edge snaps to the
         chip's right edge (and mirrored for the left). */
      .chip--refine[data-tooltip][data-tt-align='right']::after {
        left: auto;
        right: 0;
        transform: none;
      }
      .chip--refine[data-tooltip][data-tt-align='left']::after {
        left: 0;
        transform: none;
      }
      .chip--refine[data-tooltip]:hover::after,
      .chip--refine[data-tooltip]:focus-visible::after {
        opacity: 1;
      }
      .chip--refine:hover {
        background: var(--gray-15);
        color: var(--color-text);
      }
      .chip--refine:active {
        transform: scale(0.97);
      }
      .chip--refine:focus-visible {
        outline: 2px solid var(--blue-60);
        outline-offset: 2px;
      }

      /* Refine trigger (DS refine-input.css :134-154). Sits at the end
         of .ds-row after chips. Click swaps the search lupa to a close
         × and hides the label. Parent toggles triggerOpen prop to drive
         the visual state. */
      .refine-trigger {
        display: inline-flex;
        align-items: center;
        gap: var(--space-1);
        padding: 4px 6px;
        background: none;
        border: none;
        color: var(--blue-60);
        font-family: var(--font-sans);
        font-size: var(--text-xs);
        cursor: pointer;
        transition: color var(--duration-fast) var(--easing);
        white-space: nowrap;
        user-select: none;
      }
      .refine-trigger:hover {
        color: var(--blue-80);
      }
      .refine-trigger svg {
        flex-shrink: 0;
      }
      .refine-trigger .trigger-icon-close {
        display: none;
      }
      .refine-trigger--open .trigger-icon-search {
        display: none;
      }
      .refine-trigger--open .trigger-icon-close {
        display: block;
      }
      .refine-trigger--open .trigger-label {
        display: none;
      }
      .refine-trigger:focus-visible {
        outline: 2px solid var(--blue-60);
        outline-offset: 2px;
        border-radius: var(--radius-sm);
      }

      @media (prefers-reduced-motion: reduce) {
        .chip--refine,
        .chip--refine:active {
          transition: none;
          transform: none;
        }
        .chip--refine[data-tooltip]::after {
          transition: none;
        }
      }
    `,
  ];

  override render() {
    const list = getChips(this.chips, this.results, this.resolvedParams);
    if (list.length === 0 && !this.showTrigger) return nothing;
    return html`
      <div
        class="ds-row"
        @pointerover=${handleTooltipClampEvent}
        @focusin=${handleTooltipClampEvent}
      >
        ${list.map(
          (chip) => html`
            <button
              type="button"
              class="chip--refine"
              data-tooltip=${chip.prompt}
              aria-label=${chip.prompt}
              @click=${() => this.handleChipClick(chip)}
            >
              <svg
                aria-hidden="true"
                width="10"
                height="10"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M10.0129 0.5C10.5652 0.5 11.0129 0.947715 11.0129 1.5V9.01068H18.5C19.0523 9.01068 19.5 9.4584 19.5 10.0107C19.5 10.563 19.0523 11.0107 18.5 11.0107H11.0129V18.5C11.0129 19.0523 10.5652 19.5 10.0129 19.5C9.46066 19.5 9.01294 19.0523 9.01294 18.5V11.0107H1.5C0.947715 11.0107 0.5 10.563 0.5 10.0107C0.5 9.4584 0.947715 9.01068 1.5 9.01068H9.01294V1.5C9.01294 0.947715 9.46066 0.5 10.0129 0.5Z"
                  fill="currentColor"
                />
              </svg>
              ${chip.label}
            </button>
          `,
        )}
        ${this.showTrigger ? this.renderRefineTrigger() : nothing}
      </div>
    `;
  }

  private renderRefineTrigger() {
    const cls = 'refine-trigger' + (this.triggerOpen ? ' refine-trigger--open' : '');
    return html`
      <button
        type="button"
        class=${cls}
        aria-expanded=${this.triggerOpen ? 'true' : 'false'}
        @click=${this.handleTriggerClick}
      >
        <svg
          class="trigger-icon-search"
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" stroke-width="1.5" />
          <path
            d="M10.5 10.5L14 14"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          />
        </svg>
        <svg
          class="trigger-icon-close"
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M3 3l10 10M13 3L3 13"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          />
        </svg>
        <span class="trigger-label">Refine search</span>
      </button>
    `;
  }

  private handleChipClick(chip: SuggestedChip): void {
    this.dispatchEvent(
      new CustomEvent('emma-chip-click', {
        detail: { label: chip.label, prompt: chip.prompt, origin: this.origin },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleTriggerClick = (): void => {
    this.dispatchEvent(
      new CustomEvent('emma-refine-trigger-click', {
        bubbles: true,
        composed: true,
      }),
    );
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'emma-chat-quick-chips': EmmaChatQuickChips;
  }
}
